// Konstanta UI Standard Treatment. Semua nilai enum diambil dari openapi.yaml
// (components/schemas) dan HANDOFF.md — jangan menambah nilai di luar kontrak.

export const STEPS = [
  { id: 1, label: "Informasi", title: "Informasi Versi" },
  { id: 2, label: "Kondisi Klinis", title: "Kondisi Klinis" },
  { id: 3, label: "Terapi & Dosis", title: "Terapi & Dosis" },
  { id: 4, label: "Validasi", title: "Validasi" },
  { id: 5, label: "Simulasi", title: "Simulasi" },
];

const toOptions = (values) => values.map((v) => ({ label: v, value: v }));

// Adapter InputSelect: state menyimpan nilai mentah, InputSelect butuh objek opsi.
export const toOption = (options, value) =>
  options.find((o) => o.value === value) || null;
export const pickValue = (selected) => selected?.value ?? "";

// ---------------------------------------------------------------- version (VersionStatus)

export const VERSION_STATUS = {
  DRAFT: { label: "DRAFT", tone: "warning", hint: "Dapat disunting" },
  FINAL: { label: "FINAL", tone: "success", hint: "Beku — hanya baca" },
  ARCHIVE: { label: "ARCHIVE", tone: "neutral", hint: "Beku & diarsipkan — hanya baca" },
};

// ---------------------------------------------------------------- master data

// DrugWrite.medication_key enum.
export const MEDICATION_KEYS = [
  "dhp_tablet",
  "dhp_dispersible",
  "primaquin_tablet",
  "artemeter_lumefantrine",
  "artesunate_pyronaridine",
  "kina_tablet",
  "klindamisin",
  "doksisiklin",
  "tetrasiklin",
  "kina_injeksi",
  "artesunat_injeksi",
];

// RegimenCreate.dose_basis enum.
export const DOSE_BASIS = ["WEIGHT"];

// ---------------------------------------------------------------- conditions (ConditionInput)

const YES_NO = [
  { label: "Ya", value: true },
  { label: "Tidak", value: false },
];

// PatientFacts.parasite enum.
export const SPECIES = [
  { label: "Falciparum", value: "FALCIPARUM" },
  { label: "Vivax", value: "VIVAX" },
  { label: "Ovale", value: "OVALE" },
  { label: "Malariae", value: "MALARIAE" },
  { label: "Probable Knowlesi", value: "PROBABLE_KNOWLESI" },
  { label: "Mix", value: "MIX" },
];

// ConditionInput.field_code enum; nilai pilihan dari deskripsi PatientFacts.
export const CONDITION_FIELDS = [
  {
    value: "disease_degree",
    label: "Derajat penyakit",
    type: "enum",
    choices: toOptions(["Tanpa Komplikasi", "Komplikasi (Malaria Berat)"]),
  },
  { value: "recurrence", label: "Kasus", type: "enum", choices: toOptions(["Baru", "Relaps"]) },
  {
    value: "g6pd_result",
    label: "Hasil G6PD",
    type: "enum",
    choices: [
      { label: "Tidak diperiksa (NOT_DONE)", value: "NOT_DONE" },
      { label: "Normal", value: "NORMAL" },
      { label: "Intermediate", value: "INTERMEDIATE" },
      { label: "Defisiensi", value: "DEFICIENT" },
    ],
  },
  { value: "parasite", label: "Parasit", type: "species", choices: SPECIES },
  { value: "age_months", label: "Usia (bulan)", type: "number" },
  { value: "pregnancy", label: "Hamil", type: "boolean", choices: YES_NO },
  { value: "breastfeeding", label: "Menyusui", type: "boolean", choices: YES_NO },
  {
    value: "lab_checking_type",
    label: "Jenis pemeriksaan",
    type: "enum",
    choices: toOptions(["Mikroskop", "RDT", "PCR"]),
  },
];

export const fieldByValue = (value) => CONDITION_FIELDS.find((f) => f.value === value);

export const OPERATOR_LABELS = {
  EQUALS: "=",
  IN: "salah satu dari",
  GT: ">",
  GTE: "≥",
  LT: "<",
  LTE: "≤",
  BETWEEN: "antara",
  EXACTLY_ONE_OF: "tepat satu dari",
  MIX_CONTAINS_ANY: "mix berisi salah satu",
  MIX_EXCLUDES_ANY: "mix tanpa",
};

// Operator yang masuk akal per tipe field — turunan dari deskripsi ConditionInput.value.
// Semua operator tetap bagian dari enum ConditionInput.operator.
export const OPERATORS = {
  enum: ["EQUALS", "IN"],
  boolean: ["EQUALS"],
  number: ["EQUALS", "LT", "LTE", "GT", "GTE", "BETWEEN"],
  species: ["EXACTLY_ONE_OF", "MIX_CONTAINS_ANY", "MIX_EXCLUDES_ANY"],
};

export const isMultiOperator = (operator) =>
  ["IN", "EXACTLY_ONE_OF", "MIX_CONTAINS_ANY", "MIX_EXCLUDES_ANY"].includes(operator);

export const formatConditionValue = (condition) => {
  const field = fieldByValue(condition.field_code);
  const label = (v) => field?.choices?.find((c) => c.value === v)?.label ?? String(v);
  if (condition.operator === "BETWEEN") return condition.value.join(" – ");
  if (Array.isArray(condition.value)) return condition.value.map(label).join(", ");
  return label(condition.value);
};

// ---------------------------------------------------------------- therapies (TherapyInput)

export const THERAPY_OPERATORS = [
  { label: "DOSE_BAND — dosis mengikuti dosis standar", value: "DOSE_BAND" },
  { label: "PRESENT — cukup diberikan", value: "PRESENT" },
];

// Tampilan rentang berat dose band. DECIMAL dikirim sebagai string (HANDOFF §1 poin 4).
export const formatBandRange = (band) => {
  const num = (v) => (v === null || v === undefined ? null : Number(v));
  const min = num(band.min_value);
  const max = num(band.max_value);
  const left = min === null ? "(−∞" : `${band.min_inclusive ? "[" : "("}${min}`;
  const right = max === null ? "∞)" : `${max}${band.max_inclusive ? "]" : ")"}`;
  return `${left}, ${right} kg`;
};

export const formatBandDose = (band) =>
  band.dose_display ||
  (band.dose_amount !== null && band.dose_amount !== undefined
    ? `${Number(band.dose_amount)} ${band.dose_unit}`
    : `${Number(band.dose_amount_min)} – ${Number(band.dose_amount_max)} ${band.dose_unit}`);

// ---------------------------------------------------------------- simulation

export const SIMULATION_FACT_FIELDS = [
  "disease_degree",
  "recurrence",
  "g6pd_result",
  "parasite",
  "pregnancy",
  "breastfeeding",
  "lab_checking_type",
];

// Contoh request simulate dari HANDOFF.md §6.4.
export const INITIAL_PATIENT = {
  disease_degree: "Tanpa Komplikasi",
  recurrence: "Baru",
  g6pd_result: "NOT_DONE",
  parasite: ["FALCIPARUM"],
  age: "0",
  age_unit: "Bulan",
  weight_kg: "0",
  pregnancy: false,
  breastfeeding: false,
  lab_checking_type: "Mikroskop",
};
export const INITIAL_MEDICATION = Object.fromEntries(MEDICATION_KEYS.map((k) => [k, ""]));

// Check.verdict & TraceEntry.verdict.
export const VERDICT_LABELS = {
  MATCH: "Sesuai",
  SKIP: "Dilewati",
  DOSE_MISMATCH: "Dosis tidak sesuai",
  NO_BAND: "Tidak ada dosis standar",
  MISSING: "Tidak diberikan",
  GROUP_VIOLATION: "Pelanggaran exclusive group",
  UNEXPECTED_DRUG: "Obat di luar aturan",
  CLOSED_WORLD: "Obat di luar aturan",
  MATCHED: "Cocok",
  REJECTED: "Ditolak",
  SKIPPED: "Dilewati",
};

// ValidationFinding.check — label & tempat perbaikannya di UI.
export const VALIDATION_CHECKS = {
  VERSION_HAS_RULE: { label: "Versi ini sudah punya setidaknya satu rule", fix: "rules" },
  RULE_HAS_CONDITION_AND_THERAPY: { label: "Semua rule sudah punya kondisi dan terapi", fix: "step2and3" },
  NO_DELETED_ROW_REFERENCED: { label: "Tidak ada rujukan ke data yang sudah dihapus", fix: "step3" },
  THERAPY_REGIMEN_FORM: { label: "Terapi DOSE_BAND memakai aturan dosis, PRESENT tidak perlu aturan dosis", fix: "step3" },
  REFERENCED_MASTER_ACTIVE: { label: "Obat dan aturan dosis yang dipakai masih aktif", fix: "masterObat" },
  DOSE_BAND_OVERLAP: { label: "Dosis standar dalam satu aturan dosis tidak saling tumpang tindih", fix: "masterDoseBand" },
  EXCLUSIVE_GROUP_WELL_FORMED: { label: "Exclusive group berisi setidaknya 2 terapi PRESENT", fix: "step3" },
  DRUG_HAS_MEDICATION_KEY: { label: "Setiap obat sudah punya medication key", fix: "masterObat" },
  REGIMEN_HAS_DOSE_BAND: { label: "Setiap aturan dosis sudah punya dosis standar", fix: "masterDoseBand" },
};
